import type { SupabaseClient } from '@supabase/supabase-js';
import {
  createGrid,
  type ColDef,
  type GridApi,
  type GridOptions,
  type ICellRendererParams,
  type IDatasource,
  type IGetRowsParams,
} from 'ag-grid-community';
import { awaitJob } from '../../lib/await-job.js';
import { describeJobFailure } from '../../lib/job-failure.js';
import { parseUnmappedFailure, UNMAPPED_MESSAGE_PREFIX } from '../../lib/order-takeover-failure.js';
import { kauflandBackendOrderUrl, varioSalesOrderUrl, varioDocumentUrl, varioArticleUrl } from '../../lib/kaufland-links.js';
import {
  loadActivatedStorefronts,
  resolveInitialStorefront,
  storefrontFilterOptions,
} from '../../lib/storefront-selection.js';
import { currentVarioSalesChannelId } from '../../lib/vario-route-context.js';
import { openArticleDialog } from './offers-article-dialog.js';
import { openModal, createKfDrawer, createKfToast, KF_ICON, KF_LOGO_GOECOMMERCE, KF_LOGO_KAUFLAND } from '@vario-connect/frontend-design';

interface OrdersCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

// ---------------------------------------------------------------------------
// Bestellungen-Fenster (go-eCommerce-Design / kf-page): volle Tabelle + 50%-
// Detail-Drawer. Liste serverseitig paginiert (Infinite-Row-Model); Detail
// (Adressen/Positionen/Belege) lazy beim Öffnen des Drawers geladen + gecacht.
// Funktionen erhalten: Versand/Storno/Refund-Dialoge, Vario-Übernahme,
// Positions-Mapping, Belege/Tracking, Storefront-/Status-/Such-Filter.
// ---------------------------------------------------------------------------

interface MasterRow {
  kind: 'master';
  storefront: string;
  id_order: string;
  ts_created: string | null;
  buyer_email: string | null;
  total_price_cents: number | null;
  currency: string | null;
  status_agg: AggStatus;
  units_count: number;
  units_mapped_count: number;
  vario_order_id: string | null;
  vario_order_number: string | null;
  // Anreicherung pro Seite (separate Queries, da die View sie nicht liefert):
  customer_name: string | null;
  delivery_number: string | null;
  invoice_number: string | null;
  // Beleg-IDs zur Nummer: nur damit sind Lieferschein und Rechnung in der Liste nach Vario
  // verlinkbar. Die Nummer allein genügt nicht — der Deeplink adressiert die documentId.
  delivery_doc_id: string | null;
  invoice_doc_id: string | null;
  /** Beste verfügbare Trackingnummer für die Anzeige (Beleg schlägt Unit). */
  tracking: string | null;
  // Für die Doppelhaken werden die beiden Wahrheiten GETRENNT gehalten — `tracking` allein
  // taugt dafür nicht, weil es beide Quellen mischt:
  //   `order_documents.tracking_number` = in Vario erzeugtes Label (Haken 1)
  //   `order_units.tracking_number`      = Kauflands Stand (Haken 2)
  // Dieselbe Trennung bei der Rechnung: DMS-Ablage vs. Kaufland-Upload.
  tracking_vario: string | null;
  tracking_kaufland: string | null;
  invoice_dms_at: string | null;
  invoice_kaufland_id: string | null;
}

interface OrderDocumentRow {
  doc_type: 'delivery' | 'invoice' | 'delivery_invoice';
  vario_document_id: string;
  vario_document_number: string | null;
  tracking_number: string | null;
  carrier: string | null;
  created_at: string;
  kaufland_invoice_id: string | null;
}

type AggStatus =
  | 'shipped'
  | 'received'
  | 'cancelled'
  | 'need_to_be_sent'
  | 'paid'
  | 'open'
  | 'returned'
  | 'mixed'
  | 'unknown';

/** Status-Pill im neuen Design (Punkt + Text). Versand-Stati → 3 Farben + neutral. */
const STATUS_AGG_DESIGN: Record<AggStatus, { cls: string; label: string }> = {
  need_to_be_sent: { cls: 'st-todo', label: 'Zu versenden' },
  paid:            { cls: 'st-todo', label: 'Bezahlt' },
  open:            { cls: 'st-todo', label: 'Offen' },
  mixed:           { cls: 'st-todo', label: 'Gemischt' },
  shipped:         { cls: 'st-ship', label: 'Versendet' },
  received:        { cls: 'st-done', label: 'Abgeschlossen' },
  cancelled:       { cls: 'st-muted', label: 'Storniert' },
  returned:        { cls: 'st-muted', label: 'Retourniert' },
  unknown:         { cls: 'st-muted', label: '…' },
};

const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string; dbStatuses: readonly string[] }> = [
  { value: '',                label: 'Alle Status',  dbStatuses: [] },
  { value: 'need_to_be_sent', label: 'Zu versenden', dbStatuses: ['need_to_be_sent'] },
  { value: 'shipped',         label: 'Versendet',    dbStatuses: ['shipped', 'sent', 'sent_and_autopaid'] },
  { value: 'received',        label: 'Abgeschlossen', dbStatuses: ['received'] },
  { value: 'cancelled',       label: 'Storniert',    dbStatuses: ['cancelled'] },
  { value: 'returned',        label: 'Retourniert',  dbStatuses: ['returned'] },
];

function dbStatusesForFilter(value: string | null): readonly string[] {
  if (!value) return [];
  return STATUS_FILTER_OPTIONS.find((o) => o.value === value)?.dbStatuses ?? [];
}

function statusGroup(status: string): AggStatus {
  if (status === 'shipped' || status === 'sent' || status === 'sent_and_autopaid') return 'shipped';
  if (status === 'received') return 'received';
  if (status === 'cancelled') return 'cancelled';
  if (status === 'need_to_be_sent') return 'need_to_be_sent';
  if (status === 'paid') return 'paid';
  if (status === 'open') return 'open';
  if (status === 'returned') return 'returned';
  return 'unknown';
}

function aggregateStatus(statuses: Array<string | null>): AggStatus {
  const groups = statuses.filter((s): s is string => !!s).map(statusGroup);
  if (groups.length === 0) return 'unknown';
  const set = new Set(groups);
  if (set.size === 1) return groups[0]!;
  if (set.has('need_to_be_sent')) return 'need_to_be_sent';
  if (set.has('paid')) return 'paid';
  if (set.has('open')) return 'open';
  return 'mixed';
}

interface DetailRow {
  kind: 'detail';
  storefront: string;
  id_order: string;
  id_order_unit: string;
  id_offer: string | null;
  id_unit: string | null;
  ean: string | null;
  title: string | null;
  status: string | null;
  quantity: number | null;
  unit_price_cents: number | null;
  currency: string | null;
  carrier_code: string | null;
  tracking_number: string | null;
  ts_status_changed: string | null;
  vario_article_id: string | null;
  vario_article_number: string | null;
  shipping_rate_cents: number | null;
}

interface KauflandAddress {
  company_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  street?: string | null;
  house_number?: string | null;
  postcode?: string | null;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
  additional_field?: string | null;
}

interface OrderHeaderFull {
  billing_address: KauflandAddress | null;
  shipping_address: KauflandAddress | null;
  buyer_email: string | null;
  currency: string | null;
}

interface FilterState {
  storefront: string | null;
  status: string | null;
  search: string;
}

const GRID_BLOCK_SIZE = 200;
const GRID_CACHE_BLOCKS = 10;

/** Dauer der Markierung der offenen Positionszellen nach Klick auf "Artikel zuordnen".
 *  Passt zu den drei Puls-Durchlaeufen von `.vart.none.is-hint` in `kf-page.css`. */
const ASSIGN_HINT_MS = 3000;

/** Markiert Elemente kurz mit `is-hint`. Erst die Klasse abraeumen und einen Reflow
 *  erzwingen, sonst bleibt die Animation beim zweiten Aufruf stumm — der Browser fasst
 *  Entfernen und Setzen in einem Frame zusammen. */
function pulseHint(elements: ArrayLike<HTMLElement>): void {
  const list = Array.from(elements);
  for (const el of list) {
    el.classList.remove('is-hint');
    void el.offsetWidth;
    el.classList.add('is-hint');
  }
  window.setTimeout(() => list.forEach((el) => el.classList.remove('is-hint')), ASSIGN_HINT_MS);
}

/** Wie viele Order-IDs der Such-Vorfilter hoechstens an die Hauptabfrage weiterreicht. Die IDs
 *  landen als `in`-Liste in der Query-URL, deshalb eine Obergrenze. Bei einer Beleg- oder
 *  Tracking-Nummer sind es ein bis zwei Treffer; die Grenze greift nur bei sehr unscharfen
 *  Begriffen, und dann sagt die Oberflaeche das auch. */
const SEARCH_ID_LIMIT = 500;

const CANCEL_REASONS: Array<{ value: string; label: string }> = [
  { value: 'NoInventory', label: 'Kein Bestand' },
  { value: 'BuyerCancelled', label: 'Käufer-Storno' },
  { value: 'ShippingAddressUndeliverable', label: 'Adresse nicht zustellbar' },
  { value: 'WrongCatalogData', label: 'Falsche Katalogdaten' },
  { value: 'WrongPrice', label: 'Falscher Preis' },
  { value: 'DelayedInventory', label: 'Verzögerter Bestand' },
  { value: 'UndeliverableRegion', label: 'Region nicht belieferbar' },
  { value: 'NoReactionBuyer', label: 'Keine Käufer-Reaktion' },
  { value: 'GeneralAdjustment', label: 'Allgemeine Anpassung' },
];

/**
 * `value` MUSS ein gueltiger Kaufland-`carrier_code` sein — die Liste steht unter
 * <https://sellerapi.kaufland.com/?page=order-files#carrier-codes>. Die Codes sind Klartext-Namen
 * MIT Leerzeichen (`Deutsche Post`), nicht zusammengeschrieben; ein Wert daneben quittiert
 * `PATCH /order-units/{id}/send` mit 400.
 *
 * Bis 2026-08-16 stand hier `DeutschePost` — diesen Code gibt es nicht, der manuelle Versand mit
 * Deutsche Post lief also in einen 400er.
 *
 * Der automatische Weg uebersetzt die Vario-Versandart in dieselben Codes; die Tabelle dafuer
 * liegt in `apps/worker/src/lib/kaufland-carrier-codes.ts`. Wer hier einen Carrier ergaenzt,
 * ergaenzt ihn sinnvollerweise auch dort.
 */
const CARRIERS: Array<{ value: string; label: string }> = [
  { value: 'DHL', label: 'DHL' },
  { value: 'DHL Express', label: 'DHL Express' },
  { value: 'DPD', label: 'DPD' },
  { value: 'GLS', label: 'GLS' },
  { value: 'Hermes', label: 'Hermes' },
  { value: 'UPS', label: 'UPS' },
  { value: 'Fedex', label: 'FedEx' },
  { value: 'Deutsche Post', label: 'Deutsche Post' },
  { value: 'Austrian Post', label: 'Österreichische Post' },
  { value: 'Packeta', label: 'Packeta' },
  { value: 'InPost', label: 'InPost' },
  { value: 'Other', label: 'Andere' },
];

const orderKey = (m: { storefront: string; id_order: string }) => `${m.storefront}::${m.id_order}`;

const WARN_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.3 3.3 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.3a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg>';
const PLUS_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>';

/** Noch nicht in Vario UND nicht alle Positionen verknüpft. */
function hasUnmappedUnits(d: MasterRow | null | undefined): boolean {
  return !!d && d.vario_order_number == null && d.units_count > 0 && d.units_mapped_count < d.units_count;
}

/**
 * Übernahme blockiert: nicht alle Positionen verknüpft und an der Storefront KEIN Rückfall auf den
 * Vario-Standard-Verkaufskanal freigegeben (Einstellung `order_default_channel_fallback`,
 * Migration 0067). Mit Rückfall sucht der Worker die Positionen über die EAN; ob das klappt,
 * weiß erst der Job, deshalb darf der Knopf hier nicht sperren.
 */
function isBlocked(d: MasterRow | null | undefined, fallbackOn: boolean): boolean {
  return hasUnmappedUnits(d) && !fallbackOn;
}

interface OrdersSlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

/** Standalone-Slot-Renderer für den Vario-App-Tab `/#/orders` (system.apps.{appId}.tabs).
 *  Rendert die Bestellübersicht mit eigenem Kopf (wie Log) über die volle iframe-Höhe. */
export function renderOrders(ctx: OrdersSlotCtx): void {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = '';
  void renderOrdersTab(ctx.contentEl, { supabase: ctx.supabase, session: ctx.session, supabaseUrl: ctx.supabaseUrl }, { standalone: true });
}

export async function renderOrdersTab(host: HTMLElement, ctx: OrdersCtx, opts: { standalone?: boolean } = {}) {
  const standalone = !!opts.standalone;
  const appheadHtml = standalone
    ? `<header class="apphead">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
        <span class="kf-logo" title="Kaufland.de">${KF_LOGO_KAUFLAND}</span>
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">Kaufland · Bestellungen</div><h1>Bestellübersicht</h1></div>
        <div class="hactions">
          <span class="hstatus"><span class="d"></span>Kaufland verbunden</span>
          <button type="button" class="btn btn-primary" id="kl-orders-refresh" title="Bestellungen jetzt mit Kaufland synchronisieren">${KF_ICON.refresh}Aktualisieren</button>
        </div>
      </header>`
    : '';
  const refreshInFilterbar = standalone
    ? ''
    : `<button type="button" class="btn btn-primary" id="kl-orders-refresh" title="Bestellungen jetzt mit Kaufland synchronisieren">${KF_ICON.refresh}Aktualisieren</button>`;
  const filterbar = `<div class="filterbar">
      <div class="select"><select id="kl-orders-storefront" title="Storefront filtern"></select></div>
      <div class="select"><select id="kl-orders-status" title="Status filtern">${STATUS_FILTER_OPTIONS.map((o) => `<option value="${o.value}">${o.label}</option>`).join('')}</select></div>
      <div class="tbsearch">${KF_ICON.search}<input id="kl-orders-search" type="search" placeholder="Order-ID, Käufer, Auftrag, Beleg, Tracking, Artikelnr.…" /><button type="button" class="clearbtn" id="kl-orders-search-clear" aria-label="Suche leeren">${KF_ICON.close}</button></div>
      ${refreshInFilterbar}
      <div class="kl-legend-wrap">
        <button type="button" class="icon-btn" id="kl-orders-legend-btn" title="Legende anzeigen" aria-label="Legende anzeigen" aria-expanded="false">${KF_ICON.help}</button>
        <div id="kl-orders-legend-popover" class="kl-legend-popover is-wide" hidden>
          <div class="kl-legend-title">Zeilen-Farben</div>
          <ul class="kl-legend-list">
            <li><span class="kl-legend-swatch kl-legend-swatch-amber"></span><span><strong>Hellorange</strong>: nicht nach Vario übernommen, weil Positionen keinem Vario-Artikel zugeordnet sind</span></li>
            <li><span class="kl-legend-swatch kl-legend-swatch-white"></span><span><strong>Weiß</strong>: übernommen oder bereit zur Übernahme</span></li>
          </ul>
          <div class="kl-legend-title kl-legend-title--sub">Suche</div>
          <ul class="kl-legend-bullets">
            <li>Durchsucht Kaufland-Order-ID, Käufer (Name, Firma, E-Mail), Vario-Auftragsnummer, Lieferschein- und Rechnungsnummer sowie Tracking-Nummern</li>
            <li>Artikelnummern der Positionen: SKU, EAN und Vario-Artikelnummer — gefunden wird die ganze Bestellung, die eine passende Position enthält</li>
            <li>Teiltreffer genügen, Groß- und Kleinschreibung egal</li>
          </ul>
          <div class="kl-legend-title kl-legend-title--sub">Bedienung</div>
          <ul class="kl-legend-bullets">
            <li>Zeile anklicken öffnet die Bestelldetails</li>
            <li>Icons rechts: Versand anlegen · Stornieren · Retoure/Erstattung · in Kaufland öffnen</li>
            <li>Artikel zuordnen: Details öffnen und in den Positionen in der Spalte „Vario-Artikel“ auf „nicht zugeordnet“ klicken</li>
          </ul>
          <div class="kl-legend-title kl-legend-title--sub">Synchronisation</div>
          <ul class="kl-legend-bullets">
            <li>Bestellungen werden alle 10 Minuten von Kaufland abgerufen</li>
            <li>Komplettabgleich erfolgt alle zwei Stunden</li>
            <li>Manueller Abruf jederzeit über „Aktualisieren“</li>
          </ul>
        </div>
      </div>
    </div>`;
  const gridWrapStyle = standalone ? 'flex:1;min-height:0;' : 'height:min(74vh,900px);min-height:480px;flex:none;';
  // `kl-grid-compact`: gleiche Zellschrift wie im Angebote-Grid, damit beide Tabellen
  // einheitlich wirken. Definiert in kf-page.css.
  const card = `<div class="card"><div class="grid-wrap" style="${gridWrapStyle}"><div id="kl-orders-grid" class="ag-theme-vario ag-theme-quartz kl-grid-compact" style="height:100%;width:100%;"></div></div></div>`;
  host.innerHTML = standalone
    ? `<div class="kf-page" style="height:100%;display:flex;flex-direction:column;"><main class="main">${appheadHtml}${filterbar}${card}</main></div>`
    : `<div class="kf-page" style="height:auto;display:block;">${filterbar}${card}</div>`;

  const root = host.querySelector<HTMLElement>('.kf-page')!;
  if (standalone) {
    const fit = () => { root.style.height = window.innerHeight + 'px'; };
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    fit();
  }
  const toast = createKfToast(root);
  const drawer = createKfDrawer(root);

  const filters: FilterState = { storefront: null, status: null, search: '' };
  const state = {
    selectedKey: null as string | null,
    detailsCache: new Map<string, DetailRow[]>(),
    headerCache: new Map<string, OrderHeaderFull>(),
    documentsCache: new Map<string, OrderDocumentRow[]>(),
    storefrontSettings: new Map<string, StorefrontSettings>(),
  };
  let gridApi: GridApi<MasterRow> | null = null;
  const fallbackEnabled = (storefront: string | undefined): boolean =>
    storefront != null && state.storefrontSettings.get(storefront)?.defaultChannelFallback === true;

  await populateStorefrontFilter(host, ctx, filters);
  await loadStorefrontSettings(ctx, state.storefrontSettings);

  const datasource: IDatasource = {
    getRows: async (params: IGetRowsParams) => {
      try {
        const { masters, total, searchTruncated } = await loadOrdersPage(ctx, filters, params.startRow, params.endRow);
        // Nur beim ersten Block melden, sonst kaeme der Hinweis bei jedem Nachladen erneut.
        if (searchTruncated && params.startRow === 0) {
          toast('Sehr viele Treffer in Belegen, Tracking und Adressen — bitte den Suchbegriff eingrenzen.');
        }
        params.successCallback(masters, total);
      } catch (e) {
        console.error('[orders] datasource.getRows failed', e);
        params.failCallback();
      }
    },
  };

  gridApi = initGrid(host, datasource);
  bindFilters();
  bindRefreshButton();
  bindLegendPopover();

  /** Legende hinter dem `?`-Button. Verhalten 1:1 wie im Angebote-Tab: Klick toggelt,
   *  Klick ausserhalb schliesst. */
  function bindLegendPopover() {
    const btn = host.querySelector<HTMLButtonElement>('#kl-orders-legend-btn');
    const pop = host.querySelector<HTMLDivElement>('#kl-orders-legend-popover');
    if (!btn || !pop) return;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = !pop.hidden;
      pop.hidden = isOpen;
      btn.setAttribute('aria-expanded', String(!isOpen));
    });
    document.addEventListener('click', (e) => {
      if (pop.hidden) return;
      const target = e.target as Node;
      if (!pop.contains(target) && !btn.contains(target)) {
        pop.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
      }
    });
  }

  function resetGridForFilterChange() {
    drawer.close();
    state.selectedKey = null;
    state.detailsCache.clear();
    state.headerCache.clear();
    setTimeout(() => {
      gridApi?.paginationGoToFirstPage();
      gridApi?.purgeInfiniteCache();
    }, 0);
  }

  /** Frische Master-Row laden, Grid neu fetchen; wenn ihr Drawer offen ist, neu rendern. */
  async function refreshAfterMutation(storefront: string, idOrder: string) {
    state.detailsCache.delete(idOrder);
    state.headerCache.delete(idOrder);
    state.documentsCache.delete(idOrder);
    setTimeout(() => gridApi?.purgeInfiniteCache(), 0);
    const fresh = await loadSingleMaster(ctx, storefront, idOrder);
    if (fresh && state.selectedKey === orderKey(fresh)) {
      await openOrderDrawer(fresh);
    }
  }

  function bindFilters() {
    const search = host.querySelector<HTMLInputElement>('#kl-orders-search')!;
    const clearBtn = host.querySelector<HTMLButtonElement>('#kl-orders-search-clear')!;
    const updateClearVisibility = () => clearBtn.classList.toggle('show', search.value.length > 0);
    updateClearVisibility();
    let debounce: number | null = null;
    search.addEventListener('input', () => {
      updateClearVisibility();
      if (debounce !== null) window.clearTimeout(debounce);
      debounce = window.setTimeout(() => {
        filters.search = search.value;
        resetGridForFilterChange();
      }, 300) as unknown as number;
    });
    clearBtn.addEventListener('click', () => {
      search.value = '';
      updateClearVisibility();
      filters.search = '';
      resetGridForFilterChange();
      search.focus();
    });
    host.querySelector<HTMLSelectElement>('#kl-orders-status')!.addEventListener('change', (e) => {
      filters.status = (e.target as HTMLSelectElement).value || null;
      resetGridForFilterChange();
    });
    host.querySelector<HTMLSelectElement>('#kl-orders-storefront')!.addEventListener('change', (e) => {
      filters.storefront = (e.target as HTMLSelectElement).value || null;
      resetGridForFilterChange();
    });
  }

  function bindRefreshButton() {
    const btn = host.querySelector<HTMLButtonElement>('#kl-orders-refresh');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      const targets = filters.storefront ? [filters.storefront] : await listActiveStorefronts(ctx);
      if (targets.length === 0) {
        toast('Keine aktivierten Kaufland-Storefronts gefunden.');
        return;
      }
      btn.disabled = true;
      try {
        for (const s of targets) {
          const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_orders_refresh', { _storefront: s });
          if (error) throw new Error(error.message);
          const jobId = Array.isArray(data) ? (data[0]?.job_id as string) : (data as { job_id?: string } | null)?.job_id;
          if (jobId) await awaitJob(ctx.supabase, jobId, 180_000);
        }
        resetGridForFilterChange();
        toast('Bestellungen aktualisiert');
      } catch (e) {
        console.error('[orders] manual sync failed', e);
        toast(`Sync fehlgeschlagen: ${(e as Error).message}`);
      } finally {
        btn.disabled = false;
      }
    });
  }

  function initGrid(host: HTMLElement, datasource: IDatasource): GridApi<MasterRow> {
    const eGrid = host.querySelector<HTMLDivElement>('#kl-orders-grid')!;
    const cols: ColDef<MasterRow>[] = [
      { headerName: 'Land', width: 78, colId: 'land',
        cellRenderer: (p: ICellRendererParams<MasterRow>) => el(landHtml(p.data?.storefront ?? '')) },
      { headerName: 'Datum', width: 138, field: 'ts_created',
        cellRenderer: (p: ICellRendererParams<MasterRow>) =>
          el(`<span class="c-date">${escapeHtml(formatDateShort(p.data?.ts_created ?? null))}<span style="color:var(--muted)">${p.data?.ts_created ? ', ' + escapeHtml(timeShort(p.data.ts_created)) : ''}</span></span>`) },
      { headerName: 'Order-ID', width: 110, field: 'id_order',
        cellRenderer: (p: ICellRendererParams<MasterRow>) => el(`<span class="c-oid">${escapeHtml(p.data?.id_order ?? '')}</span>`) },
      { headerName: 'Kunde', flex: 1.4, minWidth: 150, field: 'customer_name',
        cellRenderer: (p: ICellRendererParams<MasterRow>) =>
          el(`<span class="c-kunde">${escapeHtml(p.data?.customer_name || p.data?.buyer_email || '—')}</span>`) },
      { headerName: 'Status', width: 168, colId: 'status',
        cellRenderer: (p: ICellRendererParams<MasterRow>) => el(statusPill(p.data?.status_agg ?? 'unknown')) },
      // `tabular-nums`: gleich breite Ziffern, damit die Betraege untereinander fluchten.
      { headerName: 'Gesamtpreis', width: 120, field: 'total_price_cents', cellClass: 'r', headerClass: 'hr',
        cellStyle: { fontVariantNumeric: 'tabular-nums' },
        cellRenderer: (p: ICellRendererParams<MasterRow>) =>
          el(`<span class="c-eur">${escapeHtml(formatCents(p.data?.total_price_cents ?? null, p.data?.currency ?? 'EUR'))}</span>`) },
      // Kein "Zuordnen"-Shortcut mehr in der Zelle: blockierte Bestellungen erkennt man
      // an der amberfarbenen Zeile (`row-block`), zugeordnet wird im Drawer ueber die
      // Positionen. Die Spalte zeigt daher immer die Vario-Auftragsnummer.
      { headerName: 'Auftrag', width: 128, colId: 'auftrag',
        cellRenderer: (p: ICellRendererParams<MasterRow>) => el(varioOrderCell(p.data)) },
      { headerName: 'Lieferschein', width: 122, colId: 'ls',
        cellRenderer: (p: ICellRendererParams<MasterRow>) => el(docCell(
          p.data?.delivery_number ?? null, p.data?.delivery_doc_id ?? null, 'Lieferschein in Vario öffnen')) },
      // Breiter als der Lieferschein: hier stehen zusätzlich die beiden Haken.
      { headerName: 'Rechnung', width: 150, colId: 're',
        cellRenderer: (p: ICellRendererParams<MasterRow>) => {
          const d = p.data;
          const nummer = docCell(d?.invoice_number ?? null, d?.invoice_doc_id ?? null, 'Rechnung in Vario öffnen');
          // Haken nur, wenn es überhaupt eine Rechnung gibt — zwei graue Haken neben einem
          // Gedankenstrich wären doppelt gesagt, dass nichts da ist.
          if (!d?.invoice_number) return el(nummer);
          const dmsAt = d.invoice_dms_at ?? null;
          const kfId = d.invoice_kaufland_id ?? null;
          return el(`<span class="kl-doc-state">${nummer}`
            + deliveryTicks(!!dmsAt, !!kfId, invoiceTickTitle(dmsAt, kfId))
            + `</span>`);
        } },
      { headerName: 'Tracking-Nr.', flex: 1, minWidth: 160, colId: 'tracking',
        cellRenderer: (p: ICellRendererParams<MasterRow>) => {
          const d = p.data;
          if (!d?.tracking) return el(`<span class="c-track none">—</span>`);
          const vario = d.tracking_vario ?? null;
          const kf = d.tracking_kaufland ?? null;
          // Wie bei der Rechnung: Haken nur, wenn eine Nummer da ist. Die Nummer selbst behält
          // ihren eigenen `title` (sie wird bei Platzmangel abgeschnitten), die Haken ihren.
          return el(`<span class="kl-doc-state">`
            + `<span class="c-track" title="${escapeHtml(d.tracking)}">${escapeHtml(d.tracking)}</span>`
            + deliveryTicks(!!vario, !!kf, trackingTickTitle(vario, kf))
            + `</span>`);
        } },
      { headerName: 'Aktionen', width: 148, colId: 'actions', pinned: 'right', sortable: false, resizable: false,
        cellStyle: { padding: '0' },
        cellRenderer: (p: ICellRendererParams<MasterRow>) => orderActionsCell(p.data) },
    ];

    const options: GridOptions<MasterRow> = {
      columnDefs: cols,
      defaultColDef: { sortable: false, filter: false, resizable: true, suppressHeaderMenuButton: true },
      rowModelType: 'infinite',
      cacheBlockSize: GRID_BLOCK_SIZE,
      maxBlocksInCache: GRID_CACHE_BLOCKS,
      infiniteInitialRowCount: 50,
      blockLoadDebounceMillis: 100,
      datasource,
      rowHeight: 44,
      headerHeight: 46,
      pagination: true,
      paginationPageSize: 50,
      paginationPageSizeSelector: [50, 100, 200],
      suppressCellFocus: true,
      getRowId: (p) => (p.data ? orderKey(p.data) : ''),
      rowClassRules: {
        // Amber: Übernahme blockiert (noch nicht in Vario UND nicht alle Positionen
        // mit einem Vario-Artikel verknüpft). Zugeordnet wird im Detail-Drawer.
        'row-block': (p) => isBlocked(p.data, fallbackEnabled(p.data?.storefront)),
        'row-sel': (p) => p.data != null && state.selectedKey === orderKey(p.data),
      },
      onCellClicked: (e) => {
        if (!e.data) return;
        const target = e.event?.target as HTMLElement | null;
        // Echte Links in der Zeile (Vario-Auftrag, Lieferschein, Rechnung) navigieren selbst.
        // Ohne diesen Guard oeffnet ein Klick BEIDES: den neuen Tab und den Detail-Drawer — der
        // Drawer stand dann beim Zurueckkommen offen, ohne dass ihn jemand angefordert hat.
        if (target?.closest('a[href]')) return;
        const actBtn = target?.closest<HTMLElement>('[data-act]');
        if (actBtn) {
          const act = actBtn.dataset.act;
          if (act === 'kaufland') return;
          if (actBtn.classList.contains('is-disabled')) return;
          const order = e.data;
          void triggerOrderAction(
            act as 'ship' | 'cancel' | 'refund',
            order,
            ctx,
            toast,
            () => void refreshAfterMutation(order.storefront, order.id_order),
          );
          return;
        }
        void openOrderDrawer(e.data);
      },
      onPaginationChanged: (ev) => {
        if (!(ev.newPage || ev.newPageSize)) return;
        if (!state.selectedKey) return;
        drawer.close();
        state.selectedKey = null;
      },
    };
    return createGrid(eGrid, options);
  }

  // -------------------------------------------------------------------------
  // Detail-Drawer (50%) — Adressen / Gesamtpreis / Vario-Status / Positionen
  // -------------------------------------------------------------------------
  async function openOrderDrawer(order: MasterRow) {
    state.selectedKey = orderKey(order);
    gridApi?.redrawRows();

    let units: DetailRow[] = state.detailsCache.get(order.id_order) ?? [];
    let header: OrderHeaderFull | null = state.headerCache.get(order.id_order) ?? null;
    let docs: OrderDocumentRow[] = state.documentsCache.get(order.id_order) ?? [];
    const needLoad = !state.detailsCache.has(order.id_order) || !state.headerCache.has(order.id_order);
    if (needLoad) {
      try {
        [units, header, docs] = await Promise.all([
          loadOrderUnits(ctx, order.storefront, order.id_order),
          loadOrderHeader(ctx, order.storefront, order.id_order),
          loadOrderDocuments(ctx, order.storefront, order.id_order),
        ]);
        state.detailsCache.set(order.id_order, units);
        state.headerCache.set(order.id_order, header);
        state.documentsCache.set(order.id_order, docs);
      } catch (e) {
        toast(`Bestelldetails konnten nicht geladen werden: ${(e as Error).message}`);
        return;
      }
    } else {
      // Belege immer frisch (Tracking/Belegnummer kommen asynchron per Webhook).
      docs = await loadOrderDocuments(ctx, order.storefront, order.id_order).catch(() => docs);
      state.documentsCache.set(order.id_order, docs);
    }
    // Immer frisch: Der letzte Übernahme-Versuch ändert sich mit jedem Klick auf „In Vario übernehmen".
    const unmappedFailure = order.vario_order_number == null
      ? await loadUnmappedTakeoverFailure(ctx, order.storefront, order.id_order)
      : null;
    if (state.selectedKey !== orderKey(order)) return;

    drawer.open({
      wide: true,
      html: buildOrderDrawerHtml(order, header, units, docs, fallbackEnabled(order.storefront), unmappedFailure),
      onClose: () => {
        if (state.selectedKey === orderKey(order)) {
          state.selectedKey = null;
          gridApi?.redrawRows();
        }
      },
      onMount: (content) => wireDrawer(content, order, units),
    });
  }

  function wireDrawer(content: HTMLElement, order: MasterRow, units: DetailRow[]) {
    // Vario-Übernahme
    const takeoverBtn = content.querySelector<HTMLButtonElement>('[data-kf-takeover]');
    takeoverBtn?.addEventListener('click', async () => {
      const missing = missingStorefrontSettings(state.storefrontSettings.get(order.storefront));
      if (missing.length > 0) {
        toast(`Storefront ${order.storefront.toUpperCase()}: bitte ${missing.join(' / ')} in den Einstellungen zuweisen.`);
        return;
      }
      takeoverBtn.disabled = true;
      takeoverBtn.classList.add('is-disabled');
      takeoverBtn.innerHTML = `${KF_ICON.refresh}Übernehme…`;
      try {
        const { data, error } = await ctx.supabase.rpc('vario_enqueue_order_create', {
          _storefront: order.storefront,
          _id_order: order.id_order,
        });
        if (error) throw new Error(error.message);
        const jobId = Array.isArray(data) && data[0] ? (data[0] as { job_id: string }).job_id : null;
        if (!jobId) throw new Error('keine job_id aus vario_enqueue_order_create');
        const result = await awaitJob(ctx.supabase, jobId, 180_000);
        if (result.status === 'failed' || result.status === 'cancelled') throw new Error(result.error ?? 'Job fehlgeschlagen');
        toast(`Bestellung ${order.id_order} in Vario übernommen.`);
        await refreshAfterMutation(order.storefront, order.id_order);
      } catch (err) {
        // Der volle Text (bei Vario-Importen mehrere hundert Zeichen Diagnose) gehört in die
        // Konsole, nicht in den Toast.
        console.error('[orders] Übernahme fehlgeschlagen', err);
        const failure = describeJobFailure(err, 'Übernahme');
        toast(failure.message);
        // Der Knopf geht in JEDEM Fehlerfall zurück in den Ausgangszustand — auch beim Timeout,
        // sonst bleibt er dauerhaft auf „Übernehme…“ stehen, obwohl niemand mehr wartet.
        takeoverBtn.disabled = false;
        takeoverBtn.classList.remove('is-disabled');
        takeoverBtn.innerHTML = `${KF_ICON.cloudUp}In Vario übernehmen`;
        // Läuft der Job weiter, holt ein späterer Refresh das Ergebnis nach.
        if (failure.stillRunning) await refreshAfterMutation(order.storefront, order.id_order);
        // Artikel ohne Vario-Gegenstück: Die Meldung je EAN gehört dauerhaft in die Bestellung,
        // nicht nur in den Toast. Neu öffnen lädt sie aus dem gescheiterten Job.
        else if (parseUnmappedFailure((err as Error)?.message) !== null) await openOrderDrawer(order);
      }
    });

    // "Artikel zuordnen" (Warnbox + Footer bei blockierter Übernahme) öffnet KEINEN
    // Dialog, sondern scrollt zur Positionsübersicht und markiert dort die offenen
    // Zellen der Spalte "Vario-Artikel". Die Zuordnung selbst startet der Nutzer per
    // Klick auf "nicht zugeordnet" — so bleibt sichtbar, welche Position gemeint ist,
    // statt dass ungefragt die erste Position aufgeht.
    content.querySelectorAll<HTMLElement>('[data-kf-assign-first]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const section = content.querySelector<HTMLElement>('[data-kf-positions]');
        const chips = section?.querySelectorAll<HTMLElement>('.vart.none');
        if (!section || !chips || chips.length === 0) {
          toast('Keine zuzuordnende Position gefunden.');
          return;
        }
        section.scrollIntoView({ behavior: 'smooth', block: 'end' });
        pulseHint(chips);
      });
    });

    // Positions-Mapping (Vario-Artikel zuweisen/ändern)
    content.querySelectorAll<HTMLElement>('[data-kf-map]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idUnit = btn.dataset.kfMap!;
        const row = units.find((u) => u.id_unit === idUnit);
        if (!row || !row.id_unit) return;
        openArticleDialog({
          supabase: ctx.supabase,
          supabaseUrl: ctx.supabaseUrl,
          session: ctx.session,
          unit: {
            storefront: row.storefront,
            idUnit: row.id_unit,
            ean: row.ean,
            title: row.title,
            varioArticleId: row.vario_article_id,
            varioArticleNumber: row.vario_article_number,
          },
          onDone: () => void refreshAfterMutation(order.storefront, order.id_order),
        });
      });
    });
  }
}

// ---------------------------------------------------------------------------
// Cell-Renderer + Drawer-HTML
// ---------------------------------------------------------------------------

function el(html: string): HTMLElement {
  const e = document.createElement('span');
  e.style.cssText = 'display:inline-flex;align-items:center;max-width:100%;min-width:0;';
  e.innerHTML = html;
  return e;
}

// Echte Inline-SVG-Flaggen (Emoji-Flaggen rendern auf Windows nur als Buchstaben-
// Box). Abgedeckt sind die Kaufland-Storefronts; Emblem-Details (CZ-Wappen etc.)
// bewusst vereinfacht auf die Tricolore/Grundform. Fallback = neutrales Grau.
const FLAG_BODY: Record<string, string> = {
  de: '<rect width="3" height="2" fill="#FFCE00"/><rect width="3" height="1.333" fill="#DD0000"/><rect width="3" height="0.667" fill="#000"/>',
  at: '<rect width="3" height="2" fill="#C8102E"/><rect y="0.667" width="3" height="0.666" fill="#fff"/>',
  pl: '<rect width="3" height="2" fill="#DC143C"/><rect width="3" height="1" fill="#fff"/>',
  cz: '<rect width="3" height="1" fill="#fff"/><rect y="1" width="3" height="1" fill="#D7141E"/><polygon points="0,0 1.5,1 0,2" fill="#11457E"/>',
  sk: '<rect width="3" height="2" fill="#EE1C25"/><rect width="3" height="1.333" fill="#fff"/><rect y="0.667" width="3" height="0.667" fill="#0B4EA2"/>',
  hr: '<rect width="3" height="2" fill="#171796"/><rect width="3" height="1.333" fill="#fff"/><rect width="3" height="0.667" fill="#FF0000"/>',
  fr: '<rect width="3" height="2" fill="#EF4135"/><rect width="2" height="2" fill="#fff"/><rect width="1" height="2" fill="#0055A4"/>',
  it: '<rect width="3" height="2" fill="#CE2B37"/><rect width="2" height="2" fill="#fff"/><rect width="1" height="2" fill="#009246"/>',
};
function flagSvg(sf: string): string {
  const body = FLAG_BODY[sf.toLowerCase()] ?? '<rect width="3" height="2" fill="#d4d4d8"/>';
  return `<svg viewBox="0 0 3 2" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">${body}</svg>`;
}

function landHtml(sf: string): string {
  if (!sf) return '';
  return `<span class="c-land"><span class="flag">${flagSvg(sf)}</span>${escapeHtml(sf.toUpperCase())}</span>`;
}

function statusPill(agg: AggStatus): string {
  const info = STATUS_AGG_DESIGN[agg] ?? STATUS_AGG_DESIGN.unknown;
  return `<span class="st ${info.cls}"><span class="pd"></span>${info.label}</span>`;
}

function varioOrderCell(d: MasterRow | undefined): string {
  if (!d?.vario_order_number) return `<span class="c-doc none">—</span>`;
  const href = varioSalesOrderUrl(d.vario_order_id);
  if (href) return `<a class="c-doc" href="${escapeHtml(href)}" target="_blank" rel="noopener" title="Vario-Auftrag öffnen">${escapeHtml(d.vario_order_number)}</a>`;
  return `<span class="c-doc">${escapeHtml(d.vario_order_number)}</span>`;
}

/**
 * Zustellhaken nach WhatsApp-Muster: zwei Haken, die einzeln von grau auf grün wechseln.
 *
 *   beide grau  — nichts passiert
 *   erster grün — in Vario erzeugt (Rechnung im DMS abgelegt / Versandlabel angelegt)
 *   beide grün  — zusätzlich an Kaufland übertragen
 *
 * Ein Haken kann nicht „übertragen ohne erzeugt" sein, deshalb ist der zweite nie grün, wenn der
 * erste grau ist. Sollte das doch je vorkommen (etwa weil der Händler direkt im Seller-Portal
 * versendet hat), zeigt die Darstellung es ehrlich an, statt es zu glätten — ein stiller
 * Zustand, den niemand erklären kann, ist schlimmer als ein sichtbar seltsamer.
 *
 * Umsetzung als ein SVG mit zwei Pfaden statt zweier Zeichen: Ein Unicode-Haken wird je
 * Plattform anders gerendert und ist nicht zuverlässig einfärbbar. Die Pfade liegen leicht
 * versetzt übereinander, wie beim Vorbild.
 *
 * `title` beschreibt den Zustand vollständig — die Haken allein sind ohne Erklärung nicht
 * eindeutig, und in einer Liste hat niemand eine Legende daneben.
 */
function deliveryTicks(created: boolean, sent: boolean, titel: string): string {
  const cls = (on: boolean) => (on ? 'kl-tick on' : 'kl-tick');
  return `<span class="kl-ticks" title="${escapeHtml(titel)}" aria-label="${escapeHtml(titel)}">`
    + `<svg viewBox="0 0 20 12" width="18" height="11" aria-hidden="true">`
    + `<path class="${cls(created)}" d="M1 7.2 4.2 10.4 10.6 2.6" />`
    + `<path class="${cls(sent)}" d="M8 7.2 11.2 10.4 17.6 2.6" />`
    + `</svg></span>`;
}

/** Zustandstext für die Rechnungs-Haken. */
function invoiceTickTitle(dmsAt: string | null, kauflandId: string | null): string {
  if (kauflandId) return 'Rechnung im DMS abgelegt und an Kaufland übertragen';
  if (dmsAt) return 'Rechnung im DMS abgelegt, noch nicht an Kaufland übertragen';
  return 'Rechnung noch nicht als PDF im DMS abgelegt';
}

/** Zustandstext für die Tracking-Haken. */
function trackingTickTitle(varioTracking: string | null, kauflandTracking: string | null): string {
  if (kauflandTracking) return 'Versandlabel erzeugt und an Kaufland übertragen';
  if (varioTracking) return 'Versandlabel in Vario erzeugt, noch nicht an Kaufland übertragen';
  return 'Noch kein Versandlabel';
}

/**
 * Belegzelle (Lieferschein/Rechnung) in der Liste. Mit `documentId` als Link nach Vario, sonst
 * als reiner Text — dasselbe Muster wie `varioOrderCell`.
 *
 * Ohne Beleg-ID bleibt es Text statt eines toten Links: Das passiert regulär, wenn die App
 * ausserhalb des Vario-iframes läuft (dann liefert `varioDocumentUrl` null, weil der Host nicht
 * bestimmbar ist).
 */
function docCell(value: string | null, documentId: string | null, titel: string): string {
  if (!value) return `<span class="c-doc none">—</span>`;
  const href = varioDocumentUrl(documentId);
  if (href) {
    return `<a class="c-doc" href="${escapeHtml(href)}" target="_blank" rel="noopener"`
      + ` title="${escapeHtml(titel)}">${escapeHtml(value)}</a>`;
  }
  return `<span class="c-doc">${escapeHtml(value)}</span>`;
}

function orderActionsCell(d: MasterRow | undefined): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'act-cell';
  wrap.style.cssText = 'display:inline-flex;gap:2px;justify-content:center;align-items:center;width:100%;height:100%;';
  if (!d) return wrap;
  const s = d.status_agg;
  const canShip = s === 'need_to_be_sent' || s === 'paid' || s === 'mixed';
  const canCancel = s !== 'shipped' && s !== 'received' && s !== 'cancelled' && s !== 'returned';
  const canRefund = s === 'shipped' || s === 'received' || s === 'paid' || s === 'need_to_be_sent' || s === 'mixed';
  // Der Kaufland-Deeplink ist ein echtes <a>, damit Mittelklick und "Link kopieren"
  // funktionieren. `data-act="kaufland"` haelt nur `onCellClicked` davon ab, zusaetzlich
  // den Drawer zu oeffnen — den Sprung erledigt der Browser selbst.
  const kauflandUrl = kauflandBackendOrderUrl(d.id_order);
  wrap.innerHTML =
    actBtn('ship', KF_ICON.truck, 'Versand anlegen', canShip, '') +
    actBtn('cancel', KF_ICON.ban, 'Stornieren', canCancel, 'danger') +
    actBtn('refund', KF_ICON.return, 'Retoure / Erstattung', canRefund, '') +
    (kauflandUrl
      ? `<a class="act-icon" data-act="kaufland" href="${escapeHtml(kauflandUrl)}" target="_blank" rel="noopener" title="In Kaufland öffnen">${KF_ICON.external}</a>`
      : actBtn('kaufland', KF_ICON.external, 'In Kaufland öffnen', false, ''));
  return wrap;
}

function actBtn(act: string, icon: string, title: string, enabled: boolean, extra: string): string {
  const cls = `act-icon${extra ? ' ' + extra : ''}${enabled ? '' : ' is-disabled'}`;
  return `<button type="button" class="${cls}" data-act="${act}" title="${escapeHtml(title)}"${enabled ? '' : ' disabled'}>${icon}</button>`;
}

const COUNTRY_NAMES: Record<string, string> = {
  DE: 'Deutschland', AT: 'Österreich', CH: 'Schweiz', FR: 'Frankreich',
  IT: 'Italien', CZ: 'Tschechische Republik', SK: 'Slowakei', PL: 'Polen',
  HR: 'Kroatien', NL: 'Niederlande', BE: 'Belgien', LU: 'Luxemburg',
  DK: 'Dänemark', SE: 'Schweden', NO: 'Norwegen', FI: 'Finnland',
  ES: 'Spanien', PT: 'Portugal', GB: 'Vereinigtes Königreich',
  IE: 'Irland', HU: 'Ungarn', SI: 'Slowenien', RO: 'Rumänien',
};
function countryLabel(code: string | null | undefined): string {
  if (!code) return '';
  const upper = code.toUpperCase();
  return COUNTRY_NAMES[upper] ?? upper;
}

function joinAddress(addr: KauflandAddress | null | undefined): string {
  if (!addr) return '';
  return [addr.street, addr.house_number].filter((s): s is string => !!s && s.trim().length > 0).join(' ').trim();
}

function deriveCustomerName(addr: KauflandAddress | null, email: string | null): string | null {
  if (addr) {
    const person = [addr.first_name, addr.last_name].filter((s): s is string => !!s && s.trim().length > 0).join(' ').trim();
    if (person) return person;
    if (addr.company_name && addr.company_name.trim()) return addr.company_name.trim();
    if (addr.additional_field && addr.additional_field.trim()) return addr.additional_field.trim();
  }
  return email || null;
}

function addressesEqual(a: KauflandAddress | null, b: KauflandAddress | null): boolean {
  if (!a || !b) return false;
  const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();
  return norm(a.first_name) === norm(b.first_name)
      && norm(a.last_name) === norm(b.last_name)
      && norm(a.company_name) === norm(b.company_name)
      && norm(a.additional_field) === norm(b.additional_field)
      && norm(a.street) === norm(b.street)
      && norm(a.house_number) === norm(b.house_number)
      && norm(a.postcode) === norm(b.postcode)
      && norm(a.city) === norm(b.city)
      && norm(a.country) === norm(b.country);
}

function addressHtml(addr: KauflandAddress | null, email: string | null): string {
  if (!addr) return `<div class="addr"><div class="identisch">Keine Adressdaten — evtl. noch im 15-Min-Sperrfenster.</div></div>`;
  const name = [addr.first_name, addr.last_name].filter((s): s is string => !!s && s.trim().length > 0).join(' ').trim() || '—';
  const addon = (addr.additional_field && addr.additional_field.trim()) ? addr.additional_field
    : (addr.company_name && addr.company_name.trim()) ? addr.company_name : '';
  const street = joinAddress(addr) || '—';
  const city = [addr.postcode, addr.city].filter((s): s is string => !!s && s.trim().length > 0).join(' ').trim() || '—';
  const country = countryLabel(addr.country);
  return `<div class="addr">
    <div class="nm">${escapeHtml(name)}</div>
    ${addon ? `<div>${escapeHtml(addon)}</div>` : ''}
    <div>${escapeHtml(street)}</div>
    <div>${escapeHtml(city)}${country ? ' · ' + escapeHtml(country) : ''}</div>
    ${email ? `<div class="mail">${escapeHtml(email)}</div>` : ''}
  </div>`;
}

function buildOrderDrawerHtml(
  order: MasterRow,
  header: OrderHeaderFull | null,
  units: DetailRow[],
  docs: OrderDocumentRow[],
  fallbackOn: boolean,
  unmappedFailure: string[] | null,
): string {
  const currency = header?.currency ?? order.currency ?? 'EUR';
  let goodsCents = 0;
  let shippingCents = 0;
  for (const u of units) {
    if (u.unit_price_cents !== null && u.quantity !== null) goodsCents += u.unit_price_cents * u.quantity;
    if (u.shipping_rate_cents !== null) shippingCents += u.shipping_rate_cents;
  }
  const totalCents = goodsCents + shippingCents || (order.total_price_cents ?? 0);
  const shippingLabel = shippingCents === 0 ? '<span class="free">Kostenlos</span>' : escapeHtml(formatCents(shippingCents, currency));

  const sfHtml = `<span class="flag">${flagSvg(order.storefront)}</span>${escapeHtml(order.storefront.toUpperCase())}`;
  const dateStr = order.ts_created ? `${formatDateShort(order.ts_created)}, ${timeShort(order.ts_created)}` : '';

  const blocked = isBlocked(order, fallbackOn);
  const viaFallback = !blocked && hasUnmappedUnits(order) && unmappedFailure === null;
  const varioBadge = order.vario_order_number
    ? `<span class="vario-badge" style="width:100%;justify-content:flex-start;"><span class="pd"></span>In Vario · ${escapeHtml(order.vario_order_number)}</span>`
    : blocked
      ? `<span class="vario-badge blocked" style="width:100%;justify-content:flex-start;"><span class="pd"></span>Übernahme blockiert</span>`
      : `<span class="vario-badge" style="width:100%;justify-content:flex-start;opacity:.65;"><span class="pd" style="background:#c7c7c7;"></span>Noch nicht in Vario</span>`;

  const sameAddr = header ? addressesEqual(header.shipping_address, header.billing_address) : false;
  const billingCol = sameAddr
    ? `<div class="identisch">identisch mit Versand</div>`
    : addressHtml(header?.billing_address ?? null, null);

  // Vario-Status (kv): Auftrag / Lieferschein / Rechnung / Tracking — aus Belegen.
  const deliveryDoc = docs.find((d) => d.doc_type === 'delivery' || d.doc_type === 'delivery_invoice');
  const invoiceDoc = docs.find((d) => d.doc_type === 'invoice' || d.doc_type === 'delivery_invoice');
  const unitTracking = units.map((u) => u.tracking_number).find((t) => !!t) ?? null;
  const docTracking = docs.map((d) => d.tracking_number).find((t) => !!t) ?? null;
  const tracking = docTracking ?? unitTracking;

  const kvRow = (k: string, v: string, muted: boolean) =>
    `<div class="kv"><span class="k">${escapeHtml(k)}</span><span class="v${muted ? ' muted' : ''}">${v}</span></div>`;
  const auftragV = order.vario_order_number
    ? (() => { const h = varioSalesOrderUrl(order.vario_order_id); return h ? `<a href="${escapeHtml(h)}" target="_blank" rel="noopener">${escapeHtml(order.vario_order_number)}</a>` : escapeHtml(order.vario_order_number); })()
    : (blocked ? '— blockiert' : '— nicht angelegt');
  // Beleg-Deeplink: Auch ein Beleg ohne Nummer ist verlinkbar — die Route braucht nur die
  // documentId. Sonst wäre gerade der auffällige Fall („(ohne Nummer)") der einzige, den man in
  // Vario nicht nachsehen kann.
  const docLink = (doc: OrderDocumentRow | undefined, label: string, titel: string): string => {
    if (!doc) return escapeHtml(label);
    const href = varioDocumentUrl(doc.vario_document_id);
    if (!href) return escapeHtml(label);
    return `<a href="${escapeHtml(href)}" target="_blank" rel="noopener"`
      + ` title="${escapeHtml(titel)}">${escapeHtml(label)}</a>`;
  };
  const lsV = docLink(
    deliveryDoc,
    deliveryDoc?.vario_document_number ?? (deliveryDoc ? '(ohne Nummer)' : '— noch keiner'),
    'Lieferschein in Vario öffnen',
  );
  const reV = invoiceDoc
    ? `${docLink(invoiceDoc, invoiceDoc.vario_document_number ?? '(ohne Nummer)', 'Rechnung in Vario öffnen')}`
      + `${invoiceDoc.kaufland_invoice_id ? ' · ✓ Kaufland' : ''}`
    : '— noch keine';
  const trkV = tracking ? escapeHtml(tracking) : (order.status_agg === 'shipped' || order.status_agg === 'received' ? '— ohne Tracking' : '— nicht versendet');

  const positions = units.length === 0
    ? `<div class="identisch">Keine Positionen.</div>`
    : `<table class="postable"><thead><tr><th>Artikel</th><th class="r">Menge</th><th class="r">Preis</th><th>Status</th><th>Vario-Artikel</th></tr></thead><tbody>
        ${units.map((u) => `<tr>
          <td class="art">${escapeHtml(u.title ?? u.ean ?? '—')}</td>
          <td class="r">${u.quantity ?? ''}</td>
          <td class="r">${escapeHtml(formatCents(u.unit_price_cents, u.currency ?? currency))}</td>
          <td class="nowrap">${statusPill(statusGroup(u.status ?? ''))}</td>
          <td>${positionVarioCell(u)}</td>
        </tr>`).join('')}
      </tbody></table>`;

  // Footer
  const kauflandUrl = kauflandBackendOrderUrl(order.id_order);
  const kauflandBtn = kauflandUrl
    ? `<a class="btn btn-ghost" href="${escapeHtml(kauflandUrl)}" target="_blank" rel="noopener">${KF_ICON.external}In Kaufland</a>`
    : '';
  let varioBtn: string;
  if (order.vario_order_id) {
    const h = varioSalesOrderUrl(order.vario_order_id);
    varioBtn = h
      ? `<a class="btn btn-primary" href="${escapeHtml(h)}" target="_blank" rel="noopener">${KF_ICON.file}Vario-Auftrag</a>`
      : `<button type="button" class="btn btn-primary is-disabled" disabled>${KF_ICON.file}Vario-Auftrag</button>`;
  } else if (blocked) {
    varioBtn = `<button type="button" class="btn btn-primary" data-kf-assign-first>${PLUS_SVG}Artikel zuordnen</button>`;
  } else {
    varioBtn = `<button type="button" class="btn btn-primary" data-kf-takeover>${KF_ICON.cloudUp}In Vario übernehmen</button>`;
  }

  return `
    <div class="dhead">
      <span class="dt">Bestelldetails</span>
      <span class="sf">${sfHtml}</span>
      <span class="oid">${escapeHtml(order.id_order)}</span>
      <span class="time">${escapeHtml(dateStr)}</span>
      <button type="button" class="dclose" data-kf-drawer-close aria-label="Schließen">${KF_ICON.close}</button>
      ${varioBadge}
    </div>
    <div class="dbody">
      ${unmappedFailure !== null ? `<div class="warnbox"><div class="wi">${WARN_SVG}</div><div><div class="wt">Übernahme gescheitert</div><div class="wd">${escapeHtml(UNMAPPED_MESSAGE_PREFIX.replace(/:$/, '.'))}${unmappedFailure.length > 0 ? `<ul style="margin:6px 0 0;padding-left:18px;">${unmappedFailure.map((z) => `<li>${escapeHtml(z)}</li>`).join('')}</ul>` : ''}<div style="margin-top:6px;">Artikel in Vario anlegen (EAN im Standard-Verkaufskanal) oder zuordnen, danach erneut übernehmen.</div></div></div></div>` : ''}
      ${viaFallback ? `<div class="warnbox"><div class="wi">${WARN_SVG}</div><div><div class="wt">Suche im Standard-Verkaufskanal</div><div class="wd">Nicht alle Positionen sind einem Vario-Artikel zugeordnet. Die Übernahme sucht sie über die EAN im Vario-Standard-Verkaufskanal. Trifft eine EAN keinen oder mehrere Artikel, bricht die Übernahme ab und nennt die EAN.</div></div></div>` : ''}
      ${blocked ? `<div class="warnbox"><div class="wi">${WARN_SVG}</div><div><div class="wt">Nicht nach Vario übernommen</div><div class="wd">Ein oder mehrere Artikel sind keinem Vario-Artikel zugeordnet. Klicke unten in den Positionen in der Spalte „Vario-Artikel“ auf „nicht zugeordnet“, um den Auftrag zu erstellen.</div><button type="button" class="wbtn" data-kf-assign-first>${PLUS_SVG}Artikel zuordnen</button></div></div>` : ''}
      <div class="dsec"><div class="dcols">
        <div><div class="lab">Versand an</div>${addressHtml(header?.shipping_address ?? null, header?.buyer_email ?? null)}</div>
        <div><div class="lab">Rechnung an</div>${billingCol}</div>
      </div></div>
      <div class="dsec">
        <div class="lab">Gesamtpreis</div>
        <div class="totrow"><span>Bestellsumme</span><span>${escapeHtml(formatCents(goodsCents, currency))}</span></div>
        <div class="totrow"><span>Versandkosten</span><span>${shippingLabel}</span></div>
        <div class="totrow sum"><span>Gesamtpreis</span><span>${escapeHtml(formatCents(totalCents, currency))}</span></div>
      </div>
      <div class="dsec">
        <div class="lab">Vario-Status</div>
        ${kvRow('Vario-Auftrag', auftragV, !order.vario_order_number)}
        ${kvRow('Lieferschein', lsV, !deliveryDoc)}
        ${kvRow('Rechnung', reV, !invoiceDoc)}
        ${kvRow('Tracking', trkV, !tracking)}
      </div>
      <div class="dsec" data-kf-positions>
        <div class="lab">Positionen</div>
        ${positions}
      </div>
    </div>
    <div class="dfoot">
      ${kauflandBtn}
      ${varioBtn}
    </div>
  `;
}

function positionVarioCell(u: DetailRow): string {
  if (u.vario_article_number) {
    // Kein Aendern-Knopf mehr: Eine bestehende Zuordnung wird in der Angebotsverwaltung
    // geaendert, nicht aus den Bestelldetails heraus (Issue #82). Die Nummer bleibt ein
    // Link in den Vario-Artikel.
    const href = varioArticleUrl(u.vario_article_id);
    return href
      ? `<a class="vart" href="${escapeHtml(href)}" target="_blank" rel="noopener">${escapeHtml(u.vario_article_number)}</a>`
      : `<span class="vart">${escapeHtml(u.vario_article_number)}</span>`;
  }
  if (u.id_unit) {
    return `<span class="vart none" data-kf-map="${escapeHtml(u.id_unit)}" title="Vario-Artikel zuweisen oder anlegen">nicht zugeordnet</span>`;
  }
  return `<span class="vart">—</span>`;
}

// ---------------------------------------------------------------------------
// Order-Aktionen (Versand / Storno / Refund) — Modals erhalten
// ---------------------------------------------------------------------------

async function triggerOrderAction(
  action: 'ship' | 'cancel' | 'refund',
  master: MasterRow,
  ctx: OrdersCtx,
  toast: (m: string) => void,
  onDone: () => void,
): Promise<void> {
  let units: DetailRow[] = [];
  try {
    units = await loadOrderUnits(ctx, master.storefront, master.id_order);
  } catch (e) {
    toast(`Positionen konnten nicht geladen werden: ${(e as Error).message}`);
    return;
  }
  const candidates = units.filter((u) => actionAvailableForUnit(action, u.status ?? ''));
  if (candidates.length === 0) {
    toast('Keine passende Position für diese Aktion gefunden.');
    return;
  }
  if (candidates.length > 1) {
    openUnitPickerDialog(action, candidates, (unit) => runUnitAction(action, ctx, unit, onDone));
    return;
  }
  runUnitAction(action, ctx, candidates[0]!, onDone);
}

function runUnitAction(
  action: 'ship' | 'cancel' | 'refund',
  ctx: OrdersCtx,
  unit: DetailRow,
  onDone: () => void,
): void {
  if (action === 'ship') openShipDialog(ctx, unit, onDone);
  else if (action === 'cancel') openCancelDialog(ctx, unit, onDone);
  else openRefundDialog(ctx, unit, onDone);
}

const UNIT_ACTION_DESIGN: Record<'ship' | 'cancel' | 'refund', { title: string; icon: string; lead: string }> = {
  ship: { title: 'Versand: Position wählen', icon: 'fa-truck-fast', lead: 'Für welche Position soll der Versand gemeldet werden?' },
  cancel: { title: 'Storno: Position wählen', icon: 'fa-ban', lead: 'Welche Position soll storniert werden?' },
  refund: { title: 'Erstattung: Position wählen', icon: 'fa-arrow-rotate-left', lead: 'Für welche Position soll erstattet werden?' },
};

/** Zwischenschritt, wenn mehrere Positionen einer Bestellung fuer dieselbe Operation in Frage
 *  kommen: Kaufland arbeitet positionsgenau, die Aktionsspalte des Grids aber auf Bestellebene.
 *  Statt die Aktion abzulehnen, fragt dieser Dialog nach der Position und uebergibt dann an den
 *  gewohnten Versand-/Storno-/Erstattungs-Dialog. Nur Positionen, die den passenden Status
 *  haben, stehen zur Wahl — die Vorauswahl trifft `actionAvailableForUnit`. */
function openUnitPickerDialog(
  action: 'ship' | 'cancel' | 'refund',
  candidates: DetailRow[],
  onPick: (unit: DetailRow) => void,
): void {
  const design = UNIT_ACTION_DESIGN[action];
  const rows = candidates
    .map((u, i) => {
      const label = u.title ?? u.ean ?? u.id_order_unit;
      const price = formatCents(u.unit_price_cents, u.currency ?? 'EUR');
      const qty = u.quantity !== null ? `${u.quantity} ×` : '';
      return `<button type="button" class="kl-unit-pick" data-kf-pick="${i}">
        <span class="kl-unit-pick-main">${escapeHtml(label ?? '—')}</span>
        <span class="kl-unit-pick-meta">${escapeHtml(u.id_order_unit)} · ${escapeHtml(qty)} ${escapeHtml(price)}</span>
      </button>`;
    })
    .join('');

  const modal = openModal({
    title: design.title,
    icon: design.icon,
    width: 520,
    body: `
      <div class="kl-modal-meta">
        <strong>${escapeHtml(candidates[0]?.id_order ?? '')}</strong>
        <div class="text-grey">${escapeHtml(design.lead)}</div>
      </div>
      <div class="kl-unit-pick-list">${rows}</div>
    `,
    footer: `<button type="button" class="v-button" data-modal-cancel>Abbrechen</button>`,
  });

  modal.el.querySelectorAll<HTMLButtonElement>('[data-kf-pick]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const unit = candidates[Number(btn.dataset.kfPick)];
      if (!unit) return;
      // Erst schliessen, dann den eigentlichen Dialog oeffnen — zwei Modals uebereinander
      // waeren weder bedienbar noch per Escape sauber aufloesbar.
      modal.close();
      onPick(unit);
    });
  });
}

function actionAvailableForUnit(action: 'ship' | 'cancel' | 'refund', status: string): boolean {
  const g = statusGroup(status);
  if (action === 'ship') return g === 'need_to_be_sent' || g === 'paid';
  if (action === 'cancel') return g !== 'shipped' && g !== 'received' && g !== 'cancelled' && g !== 'returned';
  return g === 'shipped' || g === 'received' || g === 'paid' || g === 'need_to_be_sent';
}

// ---------------------------------------------------------------------------
// Data loaders
// ---------------------------------------------------------------------------

/**
 * Fuellt das Auswahlfeld und belegt es mit dem Verkaufskanal vor, aus dem der Tab geoeffnet
 * wurde. Wird genau einmal beim Tab-Aufbau gerufen — spaetere Aufrufe wuerden die Auswahl des
 * Nutzers ueberschreiben.
 */
async function populateStorefrontFilter(host: HTMLElement, ctx: OrdersCtx, filters: FilterState) {
  const activated = await loadActivatedStorefronts(ctx.supabase);
  filters.storefront = resolveInitialStorefront(activated, currentVarioSalesChannelId());
  const list = storefrontFilterOptions(activated, []);
  const sel = host.querySelector<HTMLSelectElement>('#kl-orders-storefront')!;
  sel.innerHTML = `
    <option value="">Alle Storefronts</option>
    ${list.map((sf) => `<option value="${sf}" ${filters.storefront === sf ? 'selected' : ''}>${sf.toUpperCase()}</option>`).join('')}
  `;
}

async function listActiveStorefronts(ctx: OrdersCtx): Promise<string[]> {
  return (await loadActivatedStorefronts(ctx.supabase)).map((r) => r.storefront);
}

interface StorefrontSettings {
  /** Rückfall Standard-Verkaufskanal (Migration 0067). Fehlt die Spalte (altes Backend): false. */
  defaultChannelFallback: boolean;
  paymentMethodId: string | null;
  paymentTermId: string | null;
  deliveryMethodId: string | null;
  deliveryTermId: string | null;
}

async function loadStorefrontSettings(ctx: OrdersCtx, cache: Map<string, StorefrontSettings>): Promise<void> {
  const { data, error } = await ctx.supabase.rpc('kaufland_list_storefronts');
  if (error || !data) return;
  cache.clear();
  for (const r of data as Array<{
    storefront: string;
    vario_payment_method_id: string | null;
    vario_payment_term_id: string | null;
    vario_delivery_method_id: string | null;
    vario_delivery_term_id: string | null;
    order_default_channel_fallback?: boolean | null;
  }>) {
    cache.set(r.storefront, {
      defaultChannelFallback: r.order_default_channel_fallback === true,
      paymentMethodId: r.vario_payment_method_id ?? null,
      paymentTermId: r.vario_payment_term_id ?? null,
      deliveryMethodId: r.vario_delivery_method_id ?? null,
      deliveryTermId: r.vario_delivery_term_id ?? null,
    });
  }
}

function missingStorefrontSettings(s: StorefrontSettings | undefined): string[] {
  if (!s) return ['Zahlart', 'Zahlungsbedingung', 'Versandart', 'Lieferbedingung'];
  const missing: string[] = [];
  if (!s.paymentMethodId) missing.push('Zahlart');
  if (!s.paymentTermId) missing.push('Zahlungsbedingung');
  if (!s.deliveryMethodId) missing.push('Versandart');
  if (!s.deliveryTermId) missing.push('Lieferbedingung');
  return missing;
}

function mapMasterRow(r: Record<string, unknown>): MasterRow {
  return {
    kind: 'master',
    storefront: (r as { storefront: string }).storefront,
    id_order: (r as { id_order: string }).id_order,
    ts_created: (r as { ts_created: string | null }).ts_created ?? null,
    buyer_email: (r as { buyer_email: string | null }).buyer_email ?? null,
    total_price_cents: (r as { total_price_cents: number | null }).total_price_cents ?? null,
    currency: (r as { currency: string | null }).currency ?? null,
    status_agg: 'unknown' as AggStatus,
    units_count: Number((r as { units_count: number | null }).units_count ?? 0),
    units_mapped_count: Number((r as { units_mapped_count: number | null }).units_mapped_count ?? 0),
    vario_order_id: (r as { vario_order_id: string | null }).vario_order_id ?? null,
    vario_order_number: (r as { vario_order_number: string | null }).vario_order_number ?? null,
    customer_name: null,
    delivery_number: null,
    invoice_number: null,
    delivery_doc_id: null,
    invoice_doc_id: null,
    tracking: null,
    tracking_vario: null,
    tracking_kaufland: null,
    invoice_dms_at: null,
    invoice_kaufland_id: null,
  };
}

/**
 * Letzter Übernahme-Job dieser Bestellung, falls er an Artikeln ohne Vario-Gegenstück
 * gescheitert ist. Ein späterer Versuch (laufend oder erfolgreich) verdrängt die Meldung.
 */
async function loadUnmappedTakeoverFailure(ctx: OrdersCtx, storefront: string, idOrder: string): Promise<string[] | null> {
  const { data, error } = await ctx.supabase
    .schema('account')
    .from('jobs')
    .select('status,error')
    .eq('type', 'vario.order_create_from_kaufland')
    .eq('payload->>storefront', storefront)
    .eq('payload->>idOrder', idOrder)
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) {
    console.warn('[orders] loadUnmappedTakeoverFailure failed', error.message);
    return null;
  }
  const last = (data ?? [])[0] as { status: string; error: string | null } | undefined;
  if (!last || last.status !== 'failed') return null;
  return parseUnmappedFailure(last.error);
}

async function loadOrderDocuments(ctx: OrdersCtx, storefront: string, idOrder: string): Promise<OrderDocumentRow[]> {
  const { data, error } = await ctx.supabase
    .schema('kaufland')
    .from('order_documents')
    .select('doc_type,vario_document_id,vario_document_number,tracking_number,carrier,created_at,kaufland_invoice_id')
    .eq('storefront', storefront)
    .eq('id_order', idOrder)
    .order('created_at', { ascending: true });
  if (error) {
    console.warn('[orders] loadOrderDocuments failed', error.message);
    return [];
  }
  return (data ?? []) as OrderDocumentRow[];
}

/** Status-Aggregation + Tracking-Fallback (aus order_units) pro Order. Setzt in-place. */
async function applyStatusAggregation(ctx: OrdersCtx, masters: MasterRow[]): Promise<void> {
  if (masters.length === 0) return;
  const ids = masters.map((m) => m.id_order);
  const { data: units, error } = await ctx.supabase
    .schema('kaufland')
    .from('order_units')
    .select('id_order, storefront, status, tracking_number')
    .in('id_order', ids);
  if (error) {
    console.warn('[orders] status aggregation failed', error);
    return;
  }
  const byOrder = new Map<string, { statuses: Array<string | null>; tracking: string | null }>();
  for (const u of units ?? []) {
    const row = u as { id_order: string; storefront: string; status: string | null; tracking_number: string | null };
    const key = `${row.storefront}::${row.id_order}`;
    const entry = byOrder.get(key) ?? { statuses: [], tracking: null };
    entry.statuses.push(row.status);
    if (!entry.tracking && row.tracking_number) entry.tracking = row.tracking_number;
    byOrder.set(key, entry);
  }
  for (const m of masters) {
    const entry = byOrder.get(orderKey(m));
    if (!entry) continue;
    m.status_agg = aggregateStatus(entry.statuses);
    m.units_count = entry.statuses.length;
    m.tracking = entry.tracking;
    // Aus `order_units`, also Kauflands Stand — der zweite Haken. `m.tracking` wird gleich
    // womöglich vom Beleg-Tracking überschrieben, deshalb hier separat festhalten.
    m.tracking_kaufland = entry.tracking;
  }
}

/** Kundenname aus shipping_address (View liefert ihn nicht) — eine Query pro Seite. */
async function applyCustomerNames(ctx: OrdersCtx, masters: MasterRow[]): Promise<void> {
  if (masters.length === 0) return;
  const ids = masters.map((m) => m.id_order);
  const { data, error } = await ctx.supabase
    .schema('kaufland')
    .from('orders')
    .select('id_order, storefront, shipping_address, buyer_email')
    .in('id_order', ids);
  if (error) {
    console.warn('[orders] customer names failed', error.message);
    return;
  }
  const byKey = new Map<string, string | null>();
  for (const r of (data ?? []) as Array<{ id_order: string; storefront: string; shipping_address: KauflandAddress | null; buyer_email: string | null }>) {
    byKey.set(`${r.storefront}::${r.id_order}`, deriveCustomerName(r.shipping_address ?? null, r.buyer_email ?? null));
  }
  for (const m of masters) {
    const n = byKey.get(orderKey(m));
    if (n !== undefined) m.customer_name = n;
  }
}

/** Belegnummern (Lieferschein/Rechnung) + Tracking-Override aus order_documents — eine Query pro Seite. */
async function applyOrderDocsSummary(ctx: OrdersCtx, masters: MasterRow[]): Promise<void> {
  if (masters.length === 0) return;
  const ids = masters.map((m) => m.id_order);
  const { data, error } = await ctx.supabase
    .schema('kaufland')
    .from('order_documents')
    .select('id_order, storefront, doc_type, vario_document_id, vario_document_number, tracking_number, dms_published_at, kaufland_invoice_id')
    .in('id_order', ids);
  if (error) {
    console.warn('[orders] order docs summary failed', error.message);
    return;
  }
  interface DocSummary {
    delivery: string | null;
    deliveryId: string | null;
    invoice: string | null;
    invoiceId: string | null;
    invoiceDmsAt: string | null;
    invoiceKauflandId: string | null;
    tracking: string | null;
  }
  const byKey = new Map<string, DocSummary>();
  for (const r of (data ?? []) as Array<{ id_order: string; storefront: string; doc_type: string; vario_document_id: string | null; vario_document_number: string | null; tracking_number: string | null; dms_published_at: string | null; kaufland_invoice_id: string | null }>) {
    const key = `${r.storefront}::${r.id_order}`;
    const e = byKey.get(key) ?? {
      delivery: null, deliveryId: null, invoice: null, invoiceId: null,
      invoiceDmsAt: null, invoiceKauflandId: null, tracking: null,
    };
    // Gesteuert wird weiter über die NUMMER, nicht über die ID: sonst könnte ein Beleg ohne
    // Nummer den Platz eines späteren mit Nummer belegen. Die ID kommt aus derselben Zeile mit,
    // damit Nummer und Ziel immer zum gleichen Beleg gehören.
    if ((r.doc_type === 'delivery' || r.doc_type === 'delivery_invoice') && !e.delivery) {
      e.delivery = r.vario_document_number;
      e.deliveryId = r.vario_document_id;
    }
    if ((r.doc_type === 'invoice' || r.doc_type === 'delivery_invoice') && !e.invoice) {
      e.invoice = r.vario_document_number;
      e.invoiceId = r.vario_document_id;
      // Beide Haken-Quellen aus DERSELBEN Zeile wie die Nummer — sonst könnten Nummer und
      // Haken zu verschiedenen Belegen gehören.
      e.invoiceDmsAt = r.dms_published_at;
      e.invoiceKauflandId = r.kaufland_invoice_id;
    }
    if (!e.tracking && r.tracking_number) e.tracking = r.tracking_number;
    byKey.set(key, e);
  }
  for (const m of masters) {
    const e = byKey.get(orderKey(m));
    if (!e) continue;
    m.delivery_number = e.delivery;
    m.invoice_number = e.invoice;
    m.delivery_doc_id = e.deliveryId;
    m.invoice_doc_id = e.invoiceId;
    m.invoice_dms_at = e.invoiceDmsAt;
    m.invoice_kaufland_id = e.invoiceKauflandId;
    // Vario-Seite des Trackings (in Vario erzeugtes Label) — Haken 1. Getrennt von
    // `tracking_kaufland`, das aus `order_units` kommt.
    m.tracking_vario = e.tracking;
    if (e.tracking) m.tracking = e.tracking; // Beleg-Tracking schlägt Unit-Tracking
  }
}

async function loadSingleMaster(ctx: OrdersCtx, storefront: string, idOrder: string): Promise<MasterRow | null> {
  const { data, error } = await ctx.supabase
    .schema('kaufland')
    .from('orders_with_mapping_status')
    .select('storefront,id_order,ts_created,buyer_email,total_price_cents,currency,vario_order_id,vario_order_number,units_count,units_mapped_count')
    .eq('storefront', storefront)
    .eq('id_order', idOrder)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  const master = mapMasterRow(data as Record<string, unknown>);
  await applyStatusAggregation(ctx, [master]);
  await Promise.all([applyCustomerNames(ctx, [master]), applyOrderDocsSummary(ctx, [master])]);
  return master;
}

/** Wert fuer einen PostgREST-`or()`-Ausdruck. Ohne Anfuehrungszeichen zerlegen Komma und
 *  Punkt im Suchbegriff die Filterliste — „Muster, GmbH" wuerde als zwei Bedingungen gelesen. */
function orLiteral(value: string): string {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/** Order-IDs aus Tabellen, die NICHT in `orders_with_mapping_status` stehen: Belegnummern und
 *  Tracking aus `order_documents`, Positions-Tracking aus `order_units`, Kundenname aus dem
 *  JSON-Feld `orders.shipping_address`. Gleiches Muster wie der Statusfilter — erst IDs sammeln,
 *  dann die Hauptabfrage darauf oeffnen.
 *
 *  Jede Teilsuche faengt ihre Fehler selbst ab: Faellt eine aus (etwa weil PostgREST den
 *  JSON-Filter nicht mag), bleibt die uebrige Suche benutzbar, statt komplett zu scheitern. */
async function searchRelatedIdOrders(
  ctx: OrdersCtx,
  filters: FilterState,
  pattern: string,
): Promise<{ idOrders: string[]; truncated: boolean }> {
  const sf = filters.storefront;
  const like = orLiteral(pattern);
  const collect = async (
    label: string,
    run: () => PromiseLike<{ data: unknown[] | null; error: { message: string } | null }>,
  ): Promise<string[]> => {
    try {
      const { data, error } = await run();
      if (error) {
        console.warn(`[orders] Suche in ${label} übersprungen: ${error.message}`);
        return [];
      }
      return (data ?? []).map((r) => (r as { id_order: string }).id_order);
    } catch (e) {
      console.warn(`[orders] Suche in ${label} übersprungen: ${(e as Error).message}`);
      return [];
    }
  };

  const [docs, units, addresses] = await Promise.all([
    collect('Belegnummern/Tracking', () => {
      let q = ctx.supabase
        .schema('kaufland')
        .from('order_documents')
        .select('id_order')
        .or(`vario_document_number.ilike.${like},tracking_number.ilike.${like}`)
        .limit(SEARCH_ID_LIMIT * 2);
      if (sf) q = q.eq('storefront', sf);
      return q;
    }),
    // Positionen: Tracking und die drei Artikelnummern. Welche Nummer der Nutzer zur Hand
    // hat, ist von Fall zu Fall verschieden — SKU vom Kaufland-Angebot, EAN vom Karton,
    // Vario-Artikelnummer aus dem ERP. Deshalb alle drei, statt eine zu erraten.
    collect('Positionen (Tracking, SKU, EAN, Vario-Artikelnummer)', () => {
      let q = ctx.supabase
        .schema('kaufland')
        .from('order_units_with_mapping')
        .select('id_order')
        .or(
          `tracking_number.ilike.${like},id_offer.ilike.${like},ean.ilike.${like},vario_article_number.ilike.${like}`,
        )
        .limit(SEARCH_ID_LIMIT * 2);
      if (sf) q = q.eq('storefront', sf);
      return q;
    }),
    collect('Kundennamen', () => {
      let q = ctx.supabase
        .schema('kaufland')
        .from('orders')
        .select('id_order')
        .or(
          `shipping_address->>first_name.ilike.${like},shipping_address->>last_name.ilike.${like},shipping_address->>company_name.ilike.${like}`,
        )
        .limit(SEARCH_ID_LIMIT * 2);
      if (sf) q = q.eq('storefront', sf);
      return q;
    }),
  ]);

  const unique = Array.from(new Set([...docs, ...units, ...addresses]));
  return { idOrders: unique.slice(0, SEARCH_ID_LIMIT), truncated: unique.length > SEARCH_ID_LIMIT };
}

async function loadOrdersPage(
  ctx: OrdersCtx,
  filters: FilterState,
  startRow: number,
  endRow: number,
): Promise<{ masters: MasterRow[]; total: number; searchTruncated: boolean }> {
  let restrictedIdOrders: string[] | null = null;
  const dbStatuses = dbStatusesForFilter(filters.status);
  if (dbStatuses.length > 0) {
    let unitQ = ctx.supabase
      .schema('kaufland')
      .from('order_units')
      .select('id_order')
      .in('status', dbStatuses as string[]);
    if (filters.storefront) unitQ = unitQ.eq('storefront', filters.storefront);
    const { data: unitData, error: unitErr } = await unitQ.limit(10000);
    if (unitErr) throw new Error(unitErr.message);
    restrictedIdOrders = Array.from(new Set((unitData ?? []).map((r) => (r as { id_order: string }).id_order)));
    if (restrictedIdOrders.length === 0) return { masters: [], total: 0, searchTruncated: false };
  }

  let q = ctx.supabase
    .schema('kaufland')
    .from('orders_with_mapping_status')
    .select(
      'storefront,id_order,ts_created,buyer_email,total_price_cents,currency,vario_order_id,vario_order_number,units_count,units_mapped_count',
      { count: 'exact' },
    );
  if (filters.storefront) q = q.eq('storefront', filters.storefront);
  if (restrictedIdOrders) q = q.in('id_order', restrictedIdOrders);
  let searchTruncated = false;
  if (filters.search.trim()) {
    const pattern = `%${filters.search.trim().replace(/[%_]/g, (m) => `\\${m}`)}%`;
    const like = orLiteral(pattern);
    // Order-ID, Kaeufer-Mail und Vario-Auftragsnummer stehen in der View und lassen sich
    // direkt filtern. Belege, Tracking und Kundenname kommen als ID-Liste aus dem Vorfilter.
    const parts = [
      `id_order.ilike.${like}`,
      `buyer_email.ilike.${like}`,
      `vario_order_number.ilike.${like}`,
    ];
    const related = await searchRelatedIdOrders(ctx, filters, pattern);
    searchTruncated = related.truncated;
    if (related.idOrders.length > 0) {
      parts.push(`id_order.in.(${related.idOrders.map(orLiteral).join(',')})`);
    }
    q = q.or(parts.join(','));
  }
  q = q.order('ts_created', { ascending: false, nullsFirst: false });
  q = q.range(startRow, endRow - 1);

  const { data, count, error } = await q;
  if (error) throw new Error(error.message);

  const masters: MasterRow[] = (data ?? []).map((r) => mapMasterRow(r as Record<string, unknown>));
  await applyStatusAggregation(ctx, masters);
  await Promise.all([applyCustomerNames(ctx, masters), applyOrderDocsSummary(ctx, masters)]);

  return { masters, total: count ?? masters.length, searchTruncated };
}

async function loadOrderUnits(ctx: OrdersCtx, storefront: string, idOrder: string): Promise<DetailRow[]> {
  const { data, error } = await ctx.supabase
    .schema('kaufland')
    .from('order_units_with_mapping')
    .select(
      'storefront,id_order,id_order_unit,id_offer,id_unit,ean,title,status,quantity,unit_price_cents,currency,carrier_code,tracking_number,ts_status_changed,vario_article_id,vario_article_number',
    )
    .eq('storefront', storefront)
    .eq('id_order', idOrder)
    .order('id_order_unit', { ascending: true });
  if (error) throw new Error(error.message);

  const shippingRates = new Map<string, number | null>();
  try {
    const { data: shipData } = await ctx.supabase
      .schema('kaufland')
      .from('order_units')
      .select('id_order_unit, payload')
      .eq('storefront', storefront)
      .eq('id_order', idOrder);
    for (const row of (shipData ?? []) as Array<{ id_order_unit: string; payload: Record<string, unknown> }>) {
      const raw = row.payload?.['shipping_rate'];
      const n = typeof raw === 'string' ? Number.parseInt(raw, 10) : typeof raw === 'number' ? raw : null;
      shippingRates.set(row.id_order_unit, Number.isFinite(n!) ? (n as number) : null);
    }
  } catch (e) {
    console.warn('[orders] shipping_rate load failed', e);
  }

  return (data ?? []).map((r) => {
    const base = r as Omit<DetailRow, 'kind' | 'shipping_rate_cents'>;
    return { kind: 'detail', ...base, shipping_rate_cents: shippingRates.get(base.id_order_unit) ?? null };
  });
}

async function loadOrderHeader(ctx: OrdersCtx, storefront: string, idOrder: string): Promise<OrderHeaderFull> {
  const { data, error } = await ctx.supabase
    .schema('kaufland')
    .from('orders')
    .select('billing_address, shipping_address, buyer_email, currency')
    .eq('storefront', storefront)
    .eq('id_order', idOrder)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return { billing_address: null, shipping_address: null, buyer_email: null, currency: null };
  const row = data as {
    billing_address: KauflandAddress | null;
    shipping_address: KauflandAddress | null;
    buyer_email: string | null;
    currency: string | null;
  };
  return {
    billing_address: row.billing_address ?? null,
    shipping_address: row.shipping_address ?? null,
    buyer_email: row.buyer_email ?? null,
    currency: row.currency ?? null,
  };
}

// ---------------------------------------------------------------------------
// Formatter helpers
// ---------------------------------------------------------------------------

function formatDateShort(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function timeShort(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatCents(value: number | null | undefined, currency: string): string {
  if (value === null || value === undefined) return '';
  const code = currency && currency.length === 3 ? currency : 'EUR';
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: code }).format(value / 100);
  } catch {
    return `${(value / 100).toFixed(2)} ${code}`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

// ---------------------------------------------------------------------------
// Modal helpers — Versand / Storno / Refund (openModal, kl-*-Klassen erhalten)
// ---------------------------------------------------------------------------

function openShipDialog(ctx: OrdersCtx, row: DetailRow, onDone: () => void): void {
  const carrierOpts = CARRIERS.map((c) => `<option value="${escapeHtml(c.value)}">${escapeHtml(c.label)}</option>`).join('');
  const articleLabel = row.title ?? row.ean ?? '—';
  const modal = openModal({
    title: 'Versand bestätigen',
    icon: 'fa-truck-fast',
    width: 540,
    body: `
      <div class="kl-modal-meta">
        <strong>${escapeHtml(row.id_order)}</strong>
        <div class="text-grey">${escapeHtml(row.id_order_unit)} · ${escapeHtml(articleLabel)}</div>
      </div>
      <div class="kl-form-row">
        <label>Versanddienstleister</label>
        <div class="v-select"><select id="m-ship-carrier">${carrierOpts}</select></div>
      </div>
      <div class="kl-form-row">
        <label>Tracking-Nummer</label>
        <div class="v-input">
          <span class="v-icon fa-light fa-barcode"></span>
          <input id="m-ship-tracking" placeholder="z. B. 003404342921…" />
        </div>
        <div class="kl-form-hint">Tracking ist Pflicht, sobald der Carrier gewählt ist. Wird an Kaufland und an die Käufer-Notification gepusht.</div>
      </div>
      <div class="kl-form-hint">
        Kaufland benachrichtigt den Käufer bei jeder Versandmeldung selbst; das lässt sich über die
        API nicht abwählen.
      </div>
      <div class="kl-modal-error" id="m-ship-error" hidden></div>
    `,
    footer: `
      <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
      <button type="button" class="v-button v-button-primary" id="m-ship-go">
        <span class="v-icon fa-light fa-check" data-icon-default></span>
        Versand bestätigen
      </button>
    `,
  });

  const carrierSel = modal.el.querySelector<HTMLSelectElement>('#m-ship-carrier')!;
  const trackingInp = modal.el.querySelector<HTMLInputElement>('#m-ship-tracking')!;
  const errEl = modal.el.querySelector<HTMLDivElement>('#m-ship-error')!;
  const submitBtn = modal.el.querySelector<HTMLButtonElement>('#m-ship-go')!;
  const submitIcon = submitBtn.querySelector<HTMLElement>('[data-icon-default]');

  trackingInp.focus();
  submitBtn.addEventListener('click', async () => {
    const carrier = carrierSel.value.trim();
    const tracking = trackingInp.value.trim();
    errEl.hidden = true;
    if (!carrier) { errEl.textContent = 'Versanddienstleister wählen.'; errEl.hidden = false; return; }
    if (!tracking) { errEl.textContent = 'Tracking-Nummer ist erforderlich.'; errEl.hidden = false; return; }
    submitBtn.disabled = true;
    if (submitIcon) submitIcon.className = 'v-icon fa-light fa-spinner-third fa-spin';
    try {
      const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_order_unit_ship', {
        _storefront: row.storefront,
        _id_order_unit: row.id_order_unit,
        _carrier_code: carrier,
        _tracking_number: tracking,
        // `_notes` und `_notify_buyer` werden nicht mehr gesetzt: Kaufland lehnt beide Felder am
        // /send-Endpunkt mit 400 ab („excess property"), die RPC-Parameter haben Defaults und
        // bleiben nur bestehen, damit die Signatur stabil ist.
      });
      if (error) throw new Error(error.message);
      const jobId = Array.isArray(data) ? (data[0]?.job_id as string) : (data as { job_id?: string } | null)?.job_id;
      if (jobId) await awaitJob(ctx.supabase, jobId, 60_000);
      modal.close();
      onDone();
    } catch (e) {
      errEl.textContent = `Fehler: ${(e as Error).message}`;
      errEl.hidden = false;
      submitBtn.disabled = false;
      if (submitIcon) submitIcon.className = 'v-icon fa-light fa-check';
    }
  });
}

function openCancelDialog(ctx: OrdersCtx, row: DetailRow, onDone: () => void): void {
  const reasonOpts = CANCEL_REASONS.map((r) => `<option value="${escapeHtml(r.value)}">${escapeHtml(r.label)}</option>`).join('');
  const articleLabel = row.title ?? row.ean ?? '—';
  const modal = openModal({
    title: 'Bestellung stornieren',
    icon: 'fa-ban',
    width: 540,
    body: `
      <div class="kl-modal-meta">
        <strong>${escapeHtml(row.id_order)}</strong>
        <div class="text-grey">${escapeHtml(row.id_order_unit)} · ${escapeHtml(articleLabel)}</div>
      </div>
      <div class="kl-form-row">
        <label>Storno-Grund (Pflicht)</label>
        <div class="v-select"><select id="m-cancel-reason">${reasonOpts}</select></div>
      </div>
      <div class="kl-form-row">
        <label>Zusätzlicher Kommentar (optional)</label>
        <div class="v-input"><input id="m-cancel-note" placeholder="z. B. SKU im Wareneingang beschädigt…" /></div>
      </div>
      <div class="v-banner v-banner-warning">
        <span class="v-icon fa-light fa-triangle-exclamation"></span>
        <div>Stornos schlagen auf deine Kaufland-Verkäufer-Performance. Storno nur einsetzen, wenn die Bestellung wirklich nicht erfüllt werden kann.</div>
      </div>
      <div class="kl-modal-error" id="m-cancel-error" hidden></div>
    `,
    footer: `
      <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
      <button type="button" class="v-button kl-cancel-confirm-btn" id="m-cancel-go">
        <span class="v-icon fa-light fa-ban" data-icon-default></span>
        Stornieren
      </button>
    `,
  });

  const reasonSel = modal.el.querySelector<HTMLSelectElement>('#m-cancel-reason')!;
  const noteInp = modal.el.querySelector<HTMLInputElement>('#m-cancel-note')!;
  const errEl = modal.el.querySelector<HTMLDivElement>('#m-cancel-error')!;
  const submitBtn = modal.el.querySelector<HTMLButtonElement>('#m-cancel-go')!;
  const submitIcon = submitBtn.querySelector<HTMLElement>('[data-icon-default]');

  submitBtn.addEventListener('click', async () => {
    errEl.hidden = true;
    submitBtn.disabled = true;
    if (submitIcon) submitIcon.className = 'v-icon fa-light fa-spinner-third fa-spin';
    try {
      const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_order_unit_cancel', {
        _storefront: row.storefront,
        _id_order_unit: row.id_order_unit,
        _reason: reasonSel.value,
        _additional_info: noteInp.value.trim() || null,
      });
      if (error) throw new Error(error.message);
      const jobId = Array.isArray(data) ? (data[0]?.job_id as string) : (data as { job_id?: string } | null)?.job_id;
      if (jobId) await awaitJob(ctx.supabase, jobId, 60_000);
      modal.close();
      onDone();
    } catch (e) {
      errEl.textContent = `Fehler: ${(e as Error).message}`;
      errEl.hidden = false;
      submitBtn.disabled = false;
      if (submitIcon) submitIcon.className = 'v-icon fa-light fa-ban';
    }
  });
}

function openRefundDialog(ctx: OrdersCtx, row: DetailRow, onDone: () => void): void {
  const maxCents = row.unit_price_cents !== null && row.quantity !== null ? row.unit_price_cents * row.quantity : null;
  const currency = row.currency || 'EUR';
  const maxFormatted = maxCents !== null ? formatCents(maxCents, currency) : '—';
  const defaultEur = maxCents !== null ? (maxCents / 100).toFixed(2) : '';
  const reasonOpts = CANCEL_REASONS.map((r) => `<option value="${escapeHtml(r.value)}">${escapeHtml(r.label)}</option>`).join('');
  const articleLabel = row.title ?? row.ean ?? '—';

  const modal = openModal({
    title: 'Erstattung anlegen',
    icon: 'fa-arrow-rotate-left',
    width: 520,
    body: `
      <div class="kl-modal-meta">
        <strong>${escapeHtml(row.id_order)}</strong>
        <div class="text-grey">${escapeHtml(row.id_order_unit)} · ${escapeHtml(articleLabel)} · Maximal erstattbar: <strong>${escapeHtml(maxFormatted)}</strong></div>
      </div>
      <div class="kl-form-row">
        <label>Erstattungsbetrag</label>
        <div class="v-input">
          <span class="v-icon fa-light fa-euro-sign"></span>
          <input id="m-refund-amount" type="number" min="0.01" step="0.01" value="${escapeHtml(defaultEur)}" />
        </div>
        <div class="kl-form-hint">Teilerstattungen sind erlaubt, müssen aber ≤ Positionspreis (${escapeHtml(maxFormatted)}) sein.</div>
      </div>
      <div class="kl-form-row">
        <label>Grund</label>
        <div class="v-select"><select id="m-refund-reason">${reasonOpts}</select></div>
      </div>
      <div class="kl-modal-error" id="m-refund-error" hidden></div>
    `,
    footer: `
      <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
      <button type="button" class="v-button v-button-primary" id="m-refund-go">
        <span class="v-icon fa-light fa-arrow-rotate-left" data-icon-default></span>
        Erstattung anlegen
      </button>
    `,
  });

  const amountInp = modal.el.querySelector<HTMLInputElement>('#m-refund-amount')!;
  const reasonSel = modal.el.querySelector<HTMLSelectElement>('#m-refund-reason')!;
  const errEl = modal.el.querySelector<HTMLDivElement>('#m-refund-error')!;
  const submitBtn = modal.el.querySelector<HTMLButtonElement>('#m-refund-go')!;
  const submitIcon = submitBtn.querySelector<HTMLElement>('[data-icon-default]');

  amountInp.focus();
  amountInp.select();
  submitBtn.addEventListener('click', async () => {
    errEl.hidden = true;
    const eur = Number.parseFloat(amountInp.value.replace(',', '.'));
    if (!Number.isFinite(eur) || eur <= 0) {
      errEl.textContent = 'Bitte einen gültigen positiven Betrag eingeben.';
      errEl.hidden = false;
      return;
    }
    const cents = Math.round(eur * 100);
    if (maxCents !== null && cents > maxCents) {
      errEl.textContent = `Betrag übersteigt den Positionspreis. Maximal erstattbar: ${maxFormatted}.`;
      errEl.hidden = false;
      return;
    }
    submitBtn.disabled = true;
    if (submitIcon) submitIcon.className = 'v-icon fa-light fa-spinner-third fa-spin';
    try {
      const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_order_unit_refund', {
        _storefront: row.storefront,
        _id_order_unit: row.id_order_unit,
        _amount_cents: cents,
        _reason: reasonSel.value,
      });
      if (error) throw new Error(error.message);
      const jobId = Array.isArray(data) ? (data[0]?.job_id as string) : (data as { job_id?: string } | null)?.job_id;
      if (jobId) await awaitJob(ctx.supabase, jobId, 60_000);
      modal.close();
      onDone();
    } catch (e) {
      errEl.textContent = `Fehler: ${(e as Error).message}`;
      errEl.hidden = false;
      submitBtn.disabled = false;
      if (submitIcon) submitIcon.className = 'v-icon fa-light fa-arrow-rotate-left';
    }
  });
}
